import { Outlet, useRouterState } from "@tanstack/react-router"
import { SidebarInset, SidebarProvider, useSidebar } from "design-system/organisms/Sidebar"
import { cn } from "design-system/utils"
import { useIdentify, useTrackPageview } from "lib/mixpanel"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useGetSubscription } from "modules/subscription/hooks/useGetSubscription"
import { useEffect, useRef } from "react"
import type { AuthEmployeeResponseData } from "schema"
import { useBreadcrumbs } from "../Breadcrumbs/useBreadcrumbs"
import { SiteHeader } from "../Nav/Header"
import { AppSidebar } from "../Nav/Sidebar"

type PermutWidgetIdentifyMetadata = {
  source: "permut_app"
  employeeId: string
  employeeLocale: AuthEmployeeResponseData["locale"]
  employeeRole: string | null
  isOwner: boolean
  isManager: boolean
  organizationId: string
  organizationName: string
  organizationType: AuthEmployeeResponseData["organization"]["type"]
  companyId?: string
  companyName?: string
  subscriptionName?: string
  subscriptionCredits?: number
  subscriptionAutoRenew?: boolean
}

type PermutWidgetSubscriptionData = {
  subscription: {
    id?: string
    name?: string
    credits?: number
    autoRenew?: boolean
  } | null
}

function MobileSidebarRouteCloser() {
  const router = useRouterState()
  const { isMobile, setOpenMobile } = useSidebar()
  const routeKey = `${router.location.pathname}:${JSON.stringify(router.location.search)}`

  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }, [isMobile, routeKey, setOpenMobile])

  return null
}

function buildPermutWidgetIdentifyMetadata(
  data: AuthEmployeeResponseData,
  subscriptionData?: PermutWidgetSubscriptionData,
) {
  const subscription = subscriptionData?.subscription?.id ? subscriptionData.subscription : null

  return {
    source: "permut_app",
    employeeId: data.id,
    employeeLocale: data.locale,
    employeeRole: data.role,
    isOwner: data.isOwner,
    isManager: data.isManager,
    organizationId: data.organizationId,
    organizationName: data.organization.name,
    organizationType: data.organization.type,
    companyId: data.companyId,
    companyName: data.company?.name,
    subscriptionName: subscription?.name,
    subscriptionCredits: subscription?.credits,
    subscriptionAutoRenew: subscription?.autoRenew,
  } satisfies PermutWidgetIdentifyMetadata
}

export default function Page({ children }: React.PropsWithChildren) {
  useIdentify()
  const widgetElement = useRef<HTMLDivElement>(null)
  const trackPageview = useTrackPageview()

  const { currentCrumb } = useBreadcrumbs()
  const { data, isPending } = useAuthEmployeeQuery()
  const { data: subscriptionData, isPending: isPendingSubscription } = useGetSubscription({
    enabled: Boolean(data?.company?.hasWidget),
  })
  const router = useRouterState()
  const match = router.matches.at(-1)
  const fullPath = match?.fullPath

  useEffect(() => {
    if (match && fullPath) {
      trackPageview({
        path: fullPath,
        params: match.params,
        search: match.search ?? {},
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullPath, trackPageview])

  useEffect(() => {
    if (
      widgetElement.current &&
      !widgetElement.current.innerHTML &&
      data?.company?.hasWidget &&
      !isPendingSubscription
    ) {
      const script = document.createElement("script")

      script.setAttribute("data-hide", "true")
      script.setAttribute("data-permut", "gtgrgqfngrgq")
      script.src = `https://app.permut.com/widget.js?t=${+new Date()}`

      widgetElement.current.appendChild(script)

      const identifier = setInterval(() => {
        if ("PermutWidget" in window) {
          clearInterval(identifier)
          const metadata = buildPermutWidgetIdentifyMetadata(data, subscriptionData)

          // @ts-expect-error PermutWidget identify function
          window.PermutWidget?.identifyUser({ email: data.email, name: data.name, metadata })
        }
      }, 1000)
    }
  }, [data, isPendingSubscription, subscriptionData, widgetElement])

  if (!data || isPending) {
    return null
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden min-h-[480px] min-w-[320px]">
      <SidebarProvider>
        <MobileSidebarRouteCloser />
        <AppSidebar variant="inset" />
        <SidebarInset>
          <div className="@container/main flex flex-col overflow-auto h-full relative">
            <SiteHeader />
            <div
              className={cn(
                "flex flex-1 flex-col gap-4 p-6 pb-7",
                currentCrumb?.noPadding && "p-0",
              )}
            >
              {children ?? <Outlet />}
            </div>
          </div>
          {data.company?.hasWidget && <div ref={widgetElement} />}
        </SidebarInset>
      </SidebarProvider>
    </div>
  )
}
